import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useState, useEffect, useRef, type ChangeEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ArrowLeft, FileText, Check, AlertCircle, AlertTriangle, Clock, X, Trash2, PencilLine, Eye, MessageSquare } from 'lucide-react'
import { getSupplierProfile, submitProfileForReview, uploadSupplierDocument, type EstadoVigencia } from '@/features/proveedores/api/supplierProfile.service'
import { formatDate } from '@/shared/lib/dateUtils'
import {
  EsqueletoDetalle,
  EsqueletoLinea,
  EsqueletoTabla,
} from '@/shared/components/Esqueleto'
import PaqueteMensualTab from '@/features/proveedores/components/PaqueteMensualTab'
import { fileService } from '@/services/fileService'
import { Notification, useNotification } from '@/shared/components/Notification'
import ConfirmActionModal from '@/shared/components/ConfirmActionModal'
import { TruncatedText } from '@/shared/components/TruncatedText'
import { SAT_FISCAL_REGIMES, SAT_CFDI_USAGE } from '@/constants/supplierConstants';
import { SERVICE_TYPES, TIPOS_DE_PROVEEDOR } from '@/constants/supplierConstants'
import { config } from '@/config/env'
import FinancialDocsSection from '@/features/proveedores/components/FinancialDocsSection'
import BypassExpedienteSection from '@/features/proveedores/components/BypassExpedienteSection'
import DocumentosSolicitadosSection from '@/features/proveedores/components/DocumentosSolicitadosSection'
import FechaVencimientoModal from '@/features/proveedores/components/FechaVencimientoModal'
import { Badge, type ColorDeBadge } from '@/shared/components/Badge'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

interface SupplierProfileSummaryProps {
  title?: string;
  onBack: () => void
  onSubmitSuccess: () => void
  isApproved?: boolean
  status?: string
  onEdit?: () => void
  profileData?: SupplierProfile
  hideBanner?: boolean
  /**
   * `validUntil`: fecha de vencimiento corregida por Contraloría. Sin definir
   * no se toca la que ya tiene el documento; `null` la borra.
   */
  onDocumentAction?: (documentId: string, status: 'approved' | 'rejected' | 'observed', comment?: string, validUntil?: string | null) => Promise<void>
  onApprove?: () => void
  canApprove?: boolean
  // Muestra el botón "Documentos financieros" (solo vista interna de Compras/Contraloría).
  showFinancialDocsRequest?: boolean
  // Muestra la sección de bypass de expediente (solo vista interna).
  showBypass?: boolean
  // Muestra la sección "Documentos solicitados" (vista del proveedor).
  showRequestedDocs?: boolean
  // Vista del proveedor: habilita Editar (re-subir) / Eliminar en la tabla de documentos.
  canManageOwnDocs?: boolean
}

export interface SupplierProfile {
  general: {
    id: string
    rfc: string
    legalName: string
    commercialName: string | null
    personType: string
    serviceTypeId: string
    country: string
    state: string
    city: string
    municipality: string
    suburb: string
    postalCode: string
    street: string
    externalNumber: string
    internalNumber: string | null
    mainContactName: string
    mainContactPhone: string
    salesEmail: string
    accountsPayableEmail: string
    status: string
    createdAt: string
    updatedAt: string,
    repseNumber?: string | null
    // Días de crédito acordados (1-60). Null en proveedores dados de alta
    // antes de que existiera el campo.
    creditDays?: number | null
    /** ID interno en SAI (CVE_PROV). Null si todavía no se captura. */
    saiSupplierId?: number | null
    /** general, servicios o fletes (SP-214). */
    supplierType?: string | null
  }
  fiscal: {
    taxRegime: string | null
    cfdiUse: string | null
    taxEmail: string | null
    taxPhone: string | null
  } | null
  primaryBankAccount: {
    id: string
    accountHolderName: string | null
    bankName: string
    clabe: string
    accountNumber?: string | null
    currency: string
    status: string
    createdAt: string
    updatedAt: string
    coverUrl: string | null;
    coverOriginalName?: string | null
  } | null
  documents: Array<{
    documentType: {
      id: string
      // Codigo del catalogo (EXP_OPINION_SAT y demas): con el se decide si el
      // modal sugiere una fecha de vencimiento.
      code?: string
      name: string
      description: string
      isRequired: boolean
    }
    latestDocument: {
      id: string
      fileName: string
      fileUrl: string
      status: string
      uploadedAt: string
      reviewComment: string | null
      hasComments?: boolean
      commentsCount?: number
      // Vigencia: opcionales igual que en SupplierDocument del servicio. La tabla
      // de documentos las lee, y sin declararlas aquí el build no compila.
      validUntil?: string | null
      estadoVigencia?: EstadoVigencia
    } | null
  }>
}

const SupplierProfileSummary = ({ title, onBack, onSubmitSuccess, isApproved = false, status, onEdit, profileData: initialProfileData, hideBanner = false, onDocumentAction, onApprove, canApprove, showFinancialDocsRequest = false, showBypass = false, showRequestedDocs = false, canManageOwnDocs = false }: SupplierProfileSummaryProps) => {
  const [profileData, setProfileData] = useState<SupplierProfile | null>(initialProfileData || null)
  const [loading, setLoading] = useState(!initialProfileData)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [financialDocsModalOpen, setFinancialDocsModalOpen] = useState(false)
  // Lo reporta BypassExpedienteSection, que es quien consulta el endpoint.
  const [bypassActivo, setBypassActivo] = useState(false)

  // Docs financieros solo se solicitan a proveedores activos (aprobados).
  const isActiveSupplier = isApproved || status === 'approved'

  // El estatus real sale del perfil que ya carga este componente; el prop se
  // usa de respaldo mientras llega. Si no se conoce ninguno, no se ofrece
  // enviar: vale más un botón que tarda en aparecer que uno que no debía estar.
  const estatusExpediente = profileData?.general?.status ?? status
  const puedeEnviarAValidacion =
    estatusExpediente === 'pre_registered' || estatusExpediente === 'rejected'
  const canRequestFinancialDocs = showFinancialDocsRequest && isActiveSupplier
  const [submissionReceipt, setSubmissionReceipt] = useState<{
    folio: string
    submittedAt: string
  } | null>(null)
  // La bandeja de Contraloria entra directo al paquete del mes con ?tab=mensual;
  // sin eso caeria en Informacion General y habria que buscar el tab a mano.
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<1 | 2 | 3 | 4>(
    searchParams.get('tab') === 'mensual' ? 4 : 1,
  )

  /**
   * El paquete mensual (C-07) solo aplica a proveedores de Servicios: a General
   * y Fletes no se les pide. La numeracion de los tabs se calcula en vez de ir
   * escrita, porque el de financieros solo aparece en la vista interna y si no
   * se veria un "4." sin 3.
   */
  const esProveedorDeServicios = profileData?.general?.supplierType === 'servicios'
  // Documentos del mes va justo despues de Requeridos, como en el diseño, y
  // financieros se corre al 4 cuando los dos estan presentes.
  const numeroDelTabMensual = 3
  const numeroDelTabFinanciero = esProveedorDeServicios ? 4 : 3

  // El ?tab=mensual llega antes de saber el tipo de proveedor. Si al cargar el
  // perfil resulta que no es de Servicios, ese tab no existe y la pantalla se
  // quedaria en blanco: se regresa a Informacion General.
  useEffect(() => {
    if (activeTab === 4 && profileData?.general && !esProveedorDeServicios) {
      setActiveTab(1)
    }
  }, [activeTab, profileData, esProveedorDeServicios])

  // Document Action State
  const [docAction, setDocAction] = useState<{ id: string, type: 'approve' | 'reject' | 'observe' } | null>(null)
  // Fecha de vencimiento dentro del modal de revisión: se parte de la que
  // capturó el proveedor y Contraloría la confirma o la corrige.
  const [docActionFecha, setDocActionFecha] = useState('')
  // Archivo elegido esperando su fecha de vencimiento, al gestionar documentos propios.
  const [pendienteDeSubir, setPendienteDeSubir] = useState<{
    documentTypeId: string
    file: File
    nombre: string
    /** Codigo del catalogo: decide si el modal sugiere una fecha. */
    codigo?: string
    fechaInicial: string
  } | null>(null)
  const [rejectComment, setRejectComment] = useState('')
  const [isActionLoading, setIsActionLoading] = useState(false)

  // Comments Modal State (read-only)
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false)

  const [commentsHistory, setCommentsHistory] = useState<Array<{ id: string; comment: string; createdAt: string; user?: { firstName?: string; lastName?: string; email?: string } }>>([])
  const [isLoadingComments, setIsLoadingComments] = useState(false)

  const fetchDocumentComments = async (docId: string) => {
    try {
      setIsLoadingComments(true)
      const token = localStorage.getItem('access_token')
      const response = await fetch(`${config.apiBaseUrl}/admin/suppliers/documents/${docId}/comments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setCommentsHistory(data || [])
      } else { setCommentsHistory([]) }
    } catch { setCommentsHistory([]) }
    finally { setIsLoadingComments(false) }
  }

  const handleViewDocument = async (fileUrl: string) => {
    try {
      const blobUrl = await fileService.getViewableFileUrl(fileUrl)
      window.open(blobUrl, '_blank')
      setTimeout(() => fileService.revokeViewableFileUrl(blobUrl), 30000)
    } catch {
      // fallback directo si el proxy falla
      window.open(fileUrl, '_blank')
    }
  }

  const handleOpenComments = (docId: string) => {
    setIsCommentModalOpen(true)
    fetchDocumentComments(docId)
  }

  const { notification, showSuccess, showError, hideNotification } = useNotification()

  const getStatusBanner = () => {
    if (hideBanner) return null

    // Los documentos vencidos manda sobre el estatus del expediente: uno
    // aprobado con documentos caducos no esta al dia, y el banner verde lo hacia
    // ver en orden. Se listan por nombre para que no haya que buscarlos en la
    // tabla; la vigencia la calcula el back en `estadoVigencia` (J-02 / SP-226).
    const vencidos = documents
      .filter((doc) => doc.latestDocument?.estadoVigencia === 'vencido')
      .map((doc) => doc.documentType.name)

    if (vencidos.length > 0) {
      return (
        <div className="mb-6 p-4 border rounded-lg bg-red-50 border-red-200 text-red-800">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium">
                Tienes documentos vencidos. Actualízalo para mantener tu expediente al día.
              </p>
              <p className="text-xs mt-1 opacity-90">{vencidos.join(', ')}.</p>
            </div>
          </div>
        </div>
      )
    }

    const bannerConfig: Record<string, { message: string; helperText: string; color: string; icon: any }> = {
      approved: {
        message: 'Tu expediente ha sido aprobado',
        helperText: 'Te notificaremos por correo cualquier actualización relevante de tu expediente.',
        color: 'bg-green-50 border-green-200 text-green-800',
        icon: Check
      },
      sent_to_review: {
        message: 'Tu expediente está en revisión',
        helperText: 'Recibirás notificación por correo cuando finalice la revisión. Si hay observaciones, se habilitará nuevamente la edición para que corrijas y reenvíes tu expediente.',
        color: 'bg-yellow-50 border-yellow-200 text-yellow-800',
        icon: Clock
      },
      pending_purchases: {
        message: 'Tu expediente está en revisión de compras',
        helperText: 'Te notificaremos por correo el resultado. Si Compras detecta observaciones, se te indicará qué corregir antes de reenviar.',
        color: 'bg-yellow-50 border-yellow-200 text-yellow-800',
        icon: Clock
      },
      pending_controlling: {
        message: 'Tu expediente está en revisión de contraloría',
        helperText: 'Recibirás notificación por correo con el resultado. Si hay observaciones de Contraloría, podrás editar tu expediente para atenderlas.',
        color: 'bg-yellow-50 border-yellow-200 text-yellow-800',
        icon: Clock
      },
    }

    if (!status || !bannerConfig[status]) return null

    const config = bannerConfig[status]
    const Icon = config.icon

    return (
      <div className={`mb-6 p-4 border rounded-lg ${config.color}`}>
        <div className="flex items-start gap-2">
          <Icon className="w-5 h-5 mt-0.5" />
          <div>
            <p className="text-sm font-medium">{config.message}</p>
            <p className="text-xs mt-1 opacity-90">{config.helperText}</p>
          </div>
        </div>
      </div>
    )
  }

  const handleSubmitForReview = async () => {
    try {
      setIsSubmitting(true)
      setShowConfirmModal(false)

      const response = await submitProfileForReview()
      const fallbackFolio = profileData?.general?.id
        ? `EXP-${profileData.general.id}`
        : 'No disponible'
      setSubmissionReceipt({
        folio: response.folio || fallbackFolio,
        submittedAt: response.submittedAt || new Date().toISOString(),
      })
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al enviar expediente:', error)
      showError('Error', error?.message || error?.error || 'No se pudo enviar el expediente')
    } finally {
      setIsSubmitting(false)
    }
  }

  const abrirRevision = (
    documento: { id: string; validUntil?: string | null },
    type: 'approve' | 'reject' | 'observe',
  ) => {
    setDocAction({ id: documento.id, type })
    setDocActionFecha((documento.validUntil ?? '').slice(0, 10))
    setRejectComment('')
  }

  const handleDocumentAction = async () => {
    if (!docAction || !onDocumentAction) return

    try {
      setIsActionLoading(true)
      const status = docAction.type === 'approve' ? 'approved' : docAction.type === 'observe' ? 'observed' : 'rejected'
      const comment = (docAction.type === 'reject' || docAction.type === 'observe') ? rejectComment || undefined : undefined

      // Se manda siempre: vacía va como null y el back borra la fecha, que es
      // la forma de deshacer una captura equivocada del proveedor.
      await onDocumentAction(docAction.id, status, comment, docActionFecha || null)

      const label = status === 'approved' ? 'aprobado' : status === 'observed' ? 'marcado como observado' : 'rechazado'
      showSuccess('Documento actualizado', `El documento ha sido ${label} correctamente`)
      setDocAction(null)
      setRejectComment('')
      setDocActionFecha('')
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error updating document:', error)
      showError('Error', error.message || 'No se pudo actualizar el estado del documento')
    } finally {
      setIsActionLoading(false)
    }
  }

  useEffect(() => {
    if (initialProfileData) {
      setProfileData(initialProfileData)
      setLoading(false)
      return
    }

    const fetchProfile = async () => {
      try {
        setLoading(true)
        const response = await getSupplierProfile()
        setProfileData(response)
      } catch (errCrudo: unknown) {
        const err = errCrudo as {
          message?: string;
          statusCode?: number;
          status?: number;
          error?: string;
          response?: { status?: number; data?: { message?: string } };
        };
        console.error('Error al cargar perfil:', err)
        showError('Error', 'No se pudo cargar la información del perfil')
      } finally {
        setLoading(false)
      }
    }

    fetchProfile()
  }, [initialProfileData])

  // --- Acciones del proveedor sobre sus propios documentos (canManageOwnDocs) ---
  const editFileInputRef = useRef<HTMLInputElement | null>(null)
  const pendingEditDocTypeId = useRef<string | null>(null)
  const [ownDocUploadingId, setOwnDocUploadingId] = useState<string | null>(null)

  // Editar = re-subir el archivo del documento (PUT documents/:documentTypeId).
  const triggerEditDocument = (documentTypeId: string) => {
    pendingEditDocTypeId.current = documentTypeId
    editFileInputRef.current?.click()
  }

  const handleOwnDocFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    const documentTypeId = pendingEditDocTypeId.current
    e.target.value = ''
    if (!file || !documentTypeId) return
    const doc = documents.find((d) => d.documentType.id === documentTypeId)
    setPendienteDeSubir({
      documentTypeId,
      file,
      nombre: doc?.documentType.name ?? 'Documento',
      codigo: doc?.documentType.code,
      fechaInicial: (doc?.latestDocument?.validUntil ?? '').slice(0, 10),
    })
  }

  const confirmarSubidaPropia = async (validUntil: string | undefined) => {
    if (!pendienteDeSubir) return
    const { documentTypeId, file } = pendienteDeSubir
    try {
      setOwnDocUploadingId(documentTypeId)
      await uploadSupplierDocument(documentTypeId, file, validUntil)
      const fresh = await getSupplierProfile()
      setProfileData(fresh)
      showSuccess('Documento actualizado', 'El archivo se subió correctamente.')
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      showError('Error', err?.response?.data?.message || err?.message || 'No se pudo subir el archivo.')
    } finally {
      setOwnDocUploadingId(null)
      pendingEditDocTypeId.current = null
      setPendienteDeSubir(null)
    }
  }

  // Eliminar: aún no hay endpoint de borrado de documento en el back (pendiente Javier).
  const handleOwnDocDelete = () => {
    showError('No disponible', 'Eliminar documento estará disponible cuando exista el endpoint en el backend.')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* A-01: silueta del expediente: la tira de tabs, los datos generales
              y la tabla de documentos, que es lo que llega después. */}
          <div className="space-y-6">
            <div className="flex gap-4 border-b pb-3" style={{ borderColor: '#E0E0E0' }}>
              <EsqueletoLinea className="w-40" />
              <EsqueletoLinea className="w-48" />
              <EsqueletoLinea className="w-44" />
            </div>
            <EsqueletoDetalle campos={6} />
            <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
              <EsqueletoTabla filas={5} columnas={6} />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={onBack}
            className={estiloDeBoton('secundario', 'mb-4')}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm">
              <strong>Error:</strong> No se pudo cargar la información
            </p>
          </div>
        </div>
      </div>
    )
  }

  const { general, fiscal, primaryBankAccount, documents } = profileData
  const fiscalAddress = `${general.street} ${general.externalNumber}${general.internalNumber ? ` ${general.internalNumber}` : ''}, ${general.suburb}, C.P. ${general.postalCode}, ${general.municipality}, ${general.state}, ${general.country}`

  // Helpers para traducir códigos SAT
  const getFiscalRegimeDisplay = (code?: string | null) => {
    if (!code) return '---';
    const regime = SAT_FISCAL_REGIMES.find(r => r.code === code);
    return regime ? `${regime.code} - ${regime.name}` : code;
  };

  const getCfdiUseDisplay = (code?: string | null) => {
    if (!code) return '---';
    const cfdi = SAT_CFDI_USAGE.find(c => c.code === code);
    return cfdi ? `${cfdi.code} - ${cfdi.name}` : code;
  };

  return (
    <>
      <Notification
        type={notification.type}
        title={notification.title}
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header con título y botones */}
          <div className="mb-6">
            <button onClick={onBack} className={estiloDeBoton('secundario', 'mb-4')}>
              <ArrowLeft className="w-4 h-4" />
              Volver
            </button>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {title || 'Mi expediente'}
                </h1>
                <p className="text-sm text-gray-500 mt-1">Consulta y administra tus datos de proveedor</p>
              </div>
              {(onEdit || (!isApproved && !status)) && (
                <div className="flex items-center gap-3 flex-wrap">
                  {canRequestFinancialDocs && (
                    <button
                      onClick={() => { setActiveTab(3); setFinancialDocsModalOpen(true) }}
                      className={estiloDeBoton('primario')}
                    >
                      <FileText className="w-4 h-4" />
                      Documentos financieros
                    </button>
                  )}
                  {onEdit && (
                    <button
                      onClick={onEdit}
                      className={estiloDeBoton('primario')}
                    >
                      <PencilLine className="w-4 h-4" />
                      Editar
                    </button>
                  )}

                  {!isApproved && !status && (
                    <>
                      <button
                        onClick={() => { /* Eliminar - sin acción por ahora */ }}
                        className={estiloDeBoton('peligro')}
                      >
                        <Trash2 className="w-4 h-4" />
                        Eliminar
                      </button>
                      <button
                        onClick={onBack}
                        className={estiloDeBoton('primario')}
                      >
                        <PencilLine className="w-4 h-4" />
                        Editar
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Tabs con el patrón de configuración: subrayado en el activo.
              Antes eran botones azules, distintos al resto del sistema. Esta
              pantalla es la misma en el portal del proveedor y en el panel
              interno, así que el cambio cae en los dos. */}
          <nav
            className="flex gap-1 border-b mb-6"
            style={{ borderColor: '#E0E0E0' }}
            aria-label="Tabs"
          >
            <button
              onClick={() => setActiveTab(1)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px ${activeTab === 1
                ? 'border-[#477FFA] font-semibold'
                : 'border-transparent hover:border-gray-200'
                }`}
              style={{ color: '#1E1E1F' }}
            >
              1. Información General
            </button>
            <button
              onClick={() => setActiveTab(2)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px ${activeTab === 2
                ? 'border-[#477FFA] font-semibold'
                : 'border-transparent hover:border-gray-200'
                }`}
              style={{ color: '#1E1E1F' }}
            >
              2. Documentos Requeridos
            </button>
            {esProveedorDeServicios && (
              <button
                onClick={() => setActiveTab(4)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px ${activeTab === 4
                  ? 'border-[#477FFA] font-semibold'
                  : 'border-transparent hover:border-gray-200'
                  }`}
                style={{ color: '#1E1E1F' }}
              >
                {numeroDelTabMensual}. Documentos del mes
              </button>
            )}
            {canRequestFinancialDocs && (
              <button
                onClick={() => setActiveTab(3)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px ${activeTab === 3
                  ? 'border-[#477FFA] font-semibold'
                  : 'border-transparent hover:border-gray-200'
                  }`}
                style={{ color: '#1E1E1F' }}
              >
                {numeroDelTabFinanciero}. Documentos financieros
              </button>
            )}
          </nav>

          {/* Banner de estatus */}
          {getStatusBanner()}

          {/* Bypass de expediente (C-NEW-03) — solo vista interna */}
          {showBypass && profileData?.general?.id && (
            <BypassExpedienteSection
              supplierId={profileData.general.id}
              supplierApproved={isApproved || status === 'approved'}
              onEstadoBypass={setBypassActivo}
            />
          )}

          {/* Documentos solicitados (C-NEW-02) — vista del proveedor. Solo se
              muestra dentro del tab "Documentos Requeridos" (activeTab === 2); la
              propia sección se oculta si Contraloría no ha solicitado nada. */}
          {showRequestedDocs && activeTab === 2 && <DocumentosSolicitadosSection />}

          {/* Banner documentos pendientes - visible en ambos tabs */}
          {onApprove && !isApproved && status !== 'approved' && (() => {
            const pendingRequired = documents.filter(
              d => d.documentType.isRequired && (!d.latestDocument || d.latestDocument.status !== 'approved')
            )
            if (pendingRequired.length === 0) return null
            return (
              <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <FileText className="w-4 h-4 flex-shrink-0 text-yellow-700" />
                <span className="text-sm font-semibold text-yellow-800">
                  Documentos obligatorios pendientes de validación
                </span>
              </div>
            )
          })()}

          {/* Tab 1: Información General */}
          {activeTab === 1 && (
            <>
              {/* Datos generales */}
              <div className="bg-white rounded-lg border border-gray-200 mb-6">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Datos generales</h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">Razón social:</label>
                      <p className="text-sm text-gray-900">{general.legalName}</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">Nombre comercial:</label>
                      <p className="text-sm text-gray-900">{general.commercialName || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">Tipo de servicio:</label>
                      <p className="text-sm text-gray-900">
                        {SERVICE_TYPES.find(t => t.id === general.serviceTypeId)?.name || general.serviceTypeId}
                      </p>
                    </div>
                    {/* SP-214. El tipo decide qué documentos se le exigen, así
                        que se ve junto a los datos generales. */}
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">Tipo de proveedor:</label>
                      <p className="text-sm text-gray-900">
                        {TIPOS_DE_PROVEEDOR[general.supplierType ?? ''] ?? 'Sin definir'}
                      </p>
                    </div>
                    {/* El portal del proveedor comparte este componente, pero el back no
                        le manda el campo: undefined lo oculta, null es "sin capturar". */}
                    {general.saiSupplierId !== undefined && (
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">ID interno (SAI):</label>
                        <p className="text-sm text-gray-900">{general.saiSupplierId ?? 'Sin asignar'}</p>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">Correo de contacto:</label>
                      <p className="text-sm text-gray-900">{general.salesEmail}</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">Contacto principal:</label>
                      <p className="text-sm text-gray-900">{general.mainContactPhone}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Condiciones de pago — sección propia para que el dato no se
                  pierda entre los campos de datos generales. */}
              <div className="bg-white rounded-lg border border-gray-200 mb-6">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Condiciones de pago</h2>
                </div>
                <div className="p-6">
                  <label className="block text-sm text-gray-500 mb-1">Días de crédito:</label>
                  <p className="text-sm text-gray-900">
                    {general.creditDays != null ? `${general.creditDays} días` : 'Sin definir'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Se usa para calcular la fecha de vencimiento de las facturas del proveedor.
                  </p>
                </div>
              </div>

              {/* Información fiscal */}
              <div className="bg-white rounded-lg border border-gray-200 mb-6">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Información fiscal</h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">RFC:</label>
                      <p className="text-sm text-gray-900">{general.rfc}</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">Tipo de proveedor:</label>
                      <p className="text-sm text-gray-900">
                        {general.personType === 'corporate'
                          ? 'Persona Moral'
                          : general.personType === 'foreign'
                            ? 'Extranjero'
                            : 'Persona Física'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">Régimen fiscal:</label>
                      <p className="text-sm text-gray-900">{getFiscalRegimeDisplay(fiscal?.taxRegime)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">Uso de CFDI:</label>
                      <p className="text-sm text-gray-900">{getCfdiUseDisplay(fiscal?.cfdiUse)}</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">Teléfono:</label>
                      <p className="text-sm text-gray-900">{fiscal?.taxPhone || general.mainContactPhone}</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">Correo fiscal:</label>
                      <p className="text-sm text-gray-900">{fiscal?.taxEmail || general.salesEmail}</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Domicilio fiscal:</label>
                    <p className="text-sm text-gray-900">{fiscalAddress}</p>
                  </div>
                </div>
              </div>

              {/* Información bancaria (CON FIX DEL BUG SP-012) */}
              <div className="bg-white rounded-lg border border-gray-200 mb-6">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Información bancaria</h2>
                </div>
                <div className="p-6">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '24px' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '24px' }}>
                      <div style={{ flex: 1 }}>
                        <label className="block text-sm text-gray-500 mb-1">CLABE</label>
                        <p className="text-sm text-gray-900">{primaryBankAccount?.clabe || 'N/A'}</p>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label className="block text-sm text-gray-500 mb-1">Número de cuenta</label>
                        <p className="text-sm text-gray-900">{primaryBankAccount?.accountNumber || '—'}</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '24px' }}>
                      <div style={{ flex: 1 }}>
                        <label className="block text-sm text-gray-500 mb-1">Banco</label>
                        <p className="text-sm text-gray-900">{primaryBankAccount?.bankName || 'N/A'}</p>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label className="block text-sm text-gray-500 mb-1">Nombre del titular</label>
                        <p className="text-sm text-gray-900">{primaryBankAccount?.accountHolderName || general.legalName}</p>
                      </div>
                    </div>
                  </div>

                  {primaryBankAccount?.coverUrl && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-gray-100 pt-4 mt-2">
                      <div className="md:col-span-3">
                        <label className="block text-sm text-gray-500 mb-3">Carátula bancaria adjunta:</label>
                        <div className="inline-flex flex-col items-start">
                          {(() => {
                            // 1. Extraemos el nombre o URL para evaluar la extensión (ignorando los parámetros "?X-Amz...")
                            const nameToEvaluate = primaryBankAccount.coverOriginalName || primaryBankAccount.coverUrl;
                            const isPdfFile = nameToEvaluate.split('?')[0].toLowerCase().endsWith('.pdf');

                            // 2. Limpiamos el nombre para mostrarlo en la UI
                            const fileNameRaw = primaryBankAccount.coverOriginalName || (
                              primaryBankAccount.coverUrl.split('/').pop()?.split('?')[0] || 'Caratula_bancaria'
                            );
                            const fileName = decodeURIComponent(fileNameRaw);

                            // 3. Renderizado Condicional
                            return isPdfFile ? (
                              <a
                                href={primaryBankAccount.coverUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors bg-white shadow-sm"
                              >
                                <FileText className="w-5 h-5 text-red-500" />
                                <span className="text-sm font-medium">Ver documento PDF: {fileName}</span>
                              </a>
                            ) : (
                              <div className="border border-gray-200 rounded-lg p-2 bg-white shadow-sm inline-block">
                                <a
                                  href={primaryBankAccount.coverUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Clic para ver en grande"
                                >
                                  <img
                                    src={primaryBankAccount.coverUrl}
                                    alt={fileName}
                                    className="max-w-[200px] md:max-w-xs h-auto object-contain rounded cursor-pointer hover:opacity-90 transition-opacity"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.style.display = 'none';
                                      if (!target.nextElementSibling) {
                                        target.insertAdjacentHTML('afterend', '<span class="text-xs text-red-500 bg-red-50 px-2 py-1 rounded">Imagen no disponible</span>');
                                      }
                                    }}
                                  />
                                </a>
                                <p className="text-xs text-gray-500 mt-2 truncate max-w-[200px] text-center" title={fileName}>
                                  {fileName}
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Tab 2: Documentos Requeridos */}
          {activeTab === 2 && (
            <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB] mb-6">
              {/* Input oculto para el "Editar" (re-subir) del proveedor */}
              <input
                ref={editFileInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={handleOwnDocFileChange}
              />
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#E5E7EB]">
                  <thead className="bg-[#FAF9FF]">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider w-[20%]">Documento</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider w-[20%]">Archivo</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider w-[12%]">Fecha subida</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider w-[12%]">Fecha vencimiento</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider w-[12%]">Estatus</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider w-[10%]">Comentarios</th>
                      {(onDocumentAction || canManageOwnDocs) && (
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider w-[24%]">Acciones</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-[#E1E1E1]">
                    {documents.map((doc) => (
                      <tr key={doc.documentType.id} className="hover:bg-[#F4F7FB] transition-colors">
                        <td className="px-6 py-4 text-sm text-gray-800">
                          <TruncatedText text={doc.documentType.name} maxWidthClass="max-w-[220px]" className="text-gray-800" />
                        </td>
                        <td className="px-6 py-4">
                          {doc.latestDocument ? (
                            <button
                              onClick={() => handleViewDocument(doc.latestDocument!.fileUrl)}
                              className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                            >
                              <FileText className="w-4 h-4 flex-shrink-0" />
                              <TruncatedText text={doc.latestDocument.fileName} maxWidthClass="max-w-[200px]" />
                            </button>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {doc.latestDocument ? formatDate(doc.latestDocument.uploadedAt) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {doc.latestDocument?.validUntil ? formatDate(doc.latestDocument.validUntil) : 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          {(() => {
                            const d = doc.latestDocument
                            if (!d) return <Badge color="gris">No cargado</Badge>
                            const vig = d.estadoVigencia
                            let label = 'Cargado', color: ColorDeBadge = 'azul'
                            if (d.status === 'approved' && vig === 'vencido') { label = 'Vencido'; color = 'rojo' }
                            else if (d.status === 'approved' && vig === 'por_vencer') { label = 'Por vencer'; color = 'azul' }
                            else if (d.status === 'approved') { label = 'Aprobado'; color = 'verde' }
                            else if (d.status === 'rejected') { label = 'Rechazado'; color = 'rojo' }
                            else if ((d.status as string) === 'observed') { label = 'Observado'; color = 'naranja' }
                            return <Badge color={color}>{label}</Badge>
                          })()}
                        </td>
                        <td className="px-6 py-4">
                          {doc.latestDocument?.hasComments ? (
                            <button
                              onClick={() => handleOpenComments(doc.latestDocument!.id)}
                              className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                              <Eye className="w-4 h-4" />
                              Ver
                            </button>
                          ) : (
                            <span className="text-sm text-gray-400">N/A</span>
                          )}
                        </td>
                        {(onDocumentAction || canManageOwnDocs) && (
                          <td className="px-6 py-4">
                            {onDocumentAction ? (
                              /* Admin: revisión del documento */
                              doc.latestDocument ? (
                                doc.latestDocument.status === 'approved' ? (
                                  <span className="text-sm text-gray-400">N/A</span>
                                ) : (
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      onClick={() => abrirRevision(doc.latestDocument!, 'approve')}
                                      className={estiloDeBoton('exito')}
                                    >
                                      <Check className="w-4 h-4" />
                                      Aprobar
                                    </button>
                                    <button
                                      onClick={() => abrirRevision(doc.latestDocument!, 'observe')}
                                      disabled={doc.latestDocument.status === 'observed'}
                                      className={estiloDeBoton('secundario')}
                                    >
                                      <AlertCircle className="w-4 h-4" />
                                      Observar
                                    </button>
                                    <button
                                      onClick={() => abrirRevision(doc.latestDocument!, 'reject')}
                                      disabled={doc.latestDocument.status === 'rejected'}
                                      className={estiloDeBoton('peligro')}
                                    >
                                      <X className="w-4 h-4" />
                                      Rechazar
                                    </button>
                                  </div>
                                )
                              ) : (
                                <span className="text-sm text-gray-400">—</span>
                              )
                            ) : (
                              /* Proveedor: editar (re-subir) / eliminar su documento */
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => triggerEditDocument(doc.documentType.id)}
                                  disabled={ownDocUploadingId === doc.documentType.id}
                                  className="p-1.5 rounded hover:bg-[#1FB7EE]/10 text-[#1FB7EE] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  title="Editar / re-subir archivo"
                                >
                                  <PencilLine className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={handleOwnDocDelete}
                                  disabled={!doc.latestDocument}
                                  className="p-1.5 rounded hover:bg-[#EF4444]/10 text-[#EF4444] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                  title="Eliminar archivo"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Tab 3: Documentos financieros (C-NEW-02 / SP-210) */}
          {activeTab === 3 && profileData?.general?.id && (
            <FinancialDocsSection
              supplierId={profileData.general.id}
              requestModalOpen={financialDocsModalOpen}
              onCloseRequestModal={() => setFinancialDocsModalOpen(false)}
            />
          )}

          {/* Tab: Documentos del mes (C-07 / SP-218 y SP-219).
              El modo sale de quien esta viendo: el proveedor sube y envia a
              revision, Contraloria aprueba o rechaza documento por documento.
              `canManageOwnDocs` es lo que ya distingue el portal del panel. */}
          {activeTab === 4 && esProveedorDeServicios && (
            <PaqueteMensualTab
              modo={canManageOwnDocs ? 'proveedor' : 'contraloria'}
              supplierId={profileData?.general?.id}
            />
          )}

          {onApprove && !isApproved && status !== 'approved' && (() => {
            const pendingRequired = documents.filter(
              d => d.documentType.isRequired && (!d.latestDocument || d.latestDocument.status !== 'approved')
            )
            // Para eso existe el bypass: si Compras o Contraloria ya registraron
            // la excepcion, los documentos pendientes dejan de bloquear. Sin
            // esto el boton quedaba deshabilitado igual que sin excepcion.
            const effectiveCanApprove = bypassActivo || (canApprove && pendingRequired.length === 0)
            const titulo = bypassActivo
              ? 'Se aprobara con la excepcion de expediente registrada'
              : pendingRequired.length > 0
                ? 'Debes validar todos los documentos obligatorios primero'
                : undefined
            return (
              <div className='w-full flex justify-end my-4'>
                <button
                  onClick={onApprove}
                  disabled={!effectiveCanApprove}
                  title={titulo}
                  className={estiloDeBoton('exito')}
                >
                  <Check className="w-4 h-4" />
                  Aprobar expediente
                </button>
              </div>
            )
          })()}

          {/* Botón de envío. El back solo acepta el envío en pre_registered y
              rejected (submitForReview), así que el botón se ata a esa misma
              regla leyendo el estatus del perfil. Antes dependía solo de los
              props, y en el flujo de alta llegan vacíos: un expediente ya
              aprobado seguía viendo el botón y el aviso de enviado. */}
          {!isApproved && puedeEnviarAValidacion && (
            <>
              <div className="flex justify-end mb-6">
                <button
                  onClick={() => setShowConfirmModal(true)}
                  disabled={isSubmitting}
                  className={estiloDeBoton('primario')}
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar para validación'}
                </button>
              </div>

              <ConfirmActionModal
                isOpen={showConfirmModal}
                title="Enviar Expediente a Validación"
                message="¿Estás seguro de que deseas enviar tu expediente para validación? Una vez enviado, no podrás realizar cambios hasta que sea revisado."
                confirmText="Enviar a Validación"
                variante="primario"
                onConfirm={handleSubmitForReview}
                onCancel={() => setShowConfirmModal(false)}
                isLoading={isSubmitting}
              />
            </>
          )}

          {/* Modal de confirmación de documentos */}
          <ConfirmActionModal
            isOpen={!!docAction}
            title={
              docAction?.type === 'approve' ? "Aprobar Documento"
                : docAction?.type === 'observe' ? "Observar Documento"
                  : "Rechazar Documento"
            }
            message={(
              <div className="space-y-3">
                <p>
                  {docAction?.type === 'approve'
                    ? "¿Estás seguro de que deseas aprobar este documento?"
                    : docAction?.type === 'observe'
                      ? "Agrega una observación para que el proveedor corrija este documento."
                      : "¿Estás seguro de que deseas rechazar este documento?"}
                </p>
                {docAction?.type !== 'approve' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {docAction?.type === 'observe' ? "Observación" : "Motivo del rechazo (opcional)"}
                    </label>
                    <textarea
                      value={rejectComment}
                      onChange={(e) => setRejectComment(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder={docAction?.type === 'observe' ? "Describe qué debe corregir el proveedor..." : "Describe por qué se rechaza el documento..."}
                      rows={3}
                    />
                  </div>
                )}
                {/* Contraloría confirma o corrige la fecha que capturó el proveedor. */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha de vencimiento
                  </label>
                  <input
                    type="date"
                    value={docActionFecha}
                    onChange={(e) => setDocActionFecha(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    La capturó el proveedor al subir. Déjala vacía para borrarla.
                  </p>
                </div>
              </div>
            ) as any}
            confirmText={
              docAction?.type === 'approve' ? "Aprobar"
                : docAction?.type === 'observe' ? "Observar"
                  : "Rechazar"
            }
            variante={docAction?.type === 'approve' ? 'exito' : docAction?.type === 'observe' ? 'primario' : 'peligro'}
            onConfirm={handleDocumentAction}
            onCancel={() => {
              setDocAction(null)
              setRejectComment('')
              setDocActionFecha('')
            }}
            isLoading={isActionLoading}
          />

          <FechaVencimientoModal
            abierto={!!pendienteDeSubir}
            nombreDocumento={pendienteDeSubir?.nombre ?? ''}
            codigoDocumento={pendienteDeSubir?.codigo}
            fechaInicial={pendienteDeSubir?.fechaInicial}
            subiendo={!!ownDocUploadingId}
            onCancelar={() => setPendienteDeSubir(null)}
            onConfirmar={confirmarSubidaPropia}
          />

          {/* Comments Modal (read-only) */}
          {isCommentModalOpen && (
            <CapaDeModal>
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Comentarios del documento</h3>
                  </div>
                  <button onClick={() => { setIsCommentModalOpen(false); setCommentsHistory([]) }} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                  {isLoadingComments ? (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <p className="mt-2 text-sm text-gray-500">Cargando comentarios...</p>
                    </div>
                  ) : commentsHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <MessageSquare className="w-10 h-10 mx-auto mb-2" />
                      <p className="text-sm">No hay comentarios</p>
                    </div>
                  ) : (
                    commentsHistory.map((c) => (
                      <div key={c.id} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-700">
                            {c.user?.firstName ? `${c.user.firstName} ${c.user.lastName || ''}`.trim() : c.user?.email || 'Usuario'}
                          </span>
                          <span className="text-xs text-gray-400">{formatDate(c.createdAt)}</span>
                        </div>
                        <p className="text-sm text-gray-600">{c.comment}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </CapaDeModal>
          )}

        </div>
      </div>

      {submissionReceipt && (
        <CapaDeModal zIndex={120}>
          <div className="w-full max-w-xl bg-white rounded-xl shadow-2xl border border-gray-200">
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900">Expediente enviado correctamente</h3>
              <p className="text-sm text-gray-600 mt-1">
                Tu solicitud ya fue enviada a validación y está en proceso de revisión.
              </p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">Folio de solicitud:</span> {submissionReceipt.folio}
                </p>
                <p className="text-sm text-blue-800 mt-1">
                  <span className="font-semibold">Fecha de envío:</span>{' '}
                  {new Date(submissionReceipt.submittedAt).toLocaleString('es-MX')}
                </p>
              </div>
              <div className="text-sm text-gray-700">
                <p>¿Qué sigue?</p>
                <p className="mt-1">
                  Compras y Contraloría revisarán tu expediente. Te notificaremos cuando haya una actualización.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={async () => {
                  setSubmissionReceipt(null)
                  await onSubmitSuccess()
                }}
                className={estiloDeBoton('primario')}
              >
                Entendido
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}
    </>
  )
}

export default SupplierProfileSummary
